import React, { useState } from "react";
import moment from "moment";

import localizationApi from "./Localizations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Status, SubStatusKey, NewsItemDto, GameStatusesDto, PlatformCode } from "./Types";

import "moment/locale/pt-br";
import "moment/locale/cs";
import "moment/locale/nl";
import "moment/locale/fr";
import "moment/locale/de";
import "moment/locale/it";
import "moment/locale/ja";
import "moment/locale/ko";
import "moment/locale/es";
import "moment/locale/pl";
import "moment/locale/ru";
import "moment/locale/zh-cn";
import "moment/locale/es";
import "moment/locale/zh-tw";
import "moment/locale/tr";
import statusPageData from "./SpData";

interface Props {
    platform: GameStatusesDto;
    news: NewsItemDto[];
    locale: string;
    sp_identifier: string;
    onToggle: (platform: GameStatusesDto) => void;
}

const renderLocalizedPlatformStatus = (status: Status, numberOfIssuesDetected: number, locale: string) => {
    switch (status) {
        case Status.degraded:
            return (
                <span className={"text-right py-2 text-warning"}>
                    <small>
                        {renderPlatformStatusIcon(status)}
                        {`${numberOfIssuesDetected}`}&nbsp;
                        {localizationApi.GetLegendProblemsLocale(locale)}
                    </small>
                </span>
            );

        case Status.interrupted:
            return (
                <span className={"text-right py-2 text-danger"}>
                    {renderPlatformStatusIcon(status)}
                    <small>{localizationApi.GetLegendOutageLocale(locale)}</small>
                </span>
            );
        case Status.maintenance:
            return (
                <span className={"text-right py-2 text-warning"}>
                    {renderPlatformStatusIcon(status)}
                    <small>{localizationApi.GetLegendMaintenanceLocale(locale)}</small>
                </span>
            );
        case Status.online:
            return (
                <span className={"text-right py-2 text-success"}>
                    {renderPlatformStatusIcon(status)}
                    <small>{localizationApi.GetLegendOperationalLocale(locale)}</small>
                </span>
            );
        default:
            return null;
    }
};

const renderPlatformStatusIcon = (status: Status) => {
    switch (status) {
        case Status.interrupted:
            return <FontAwesomeIcon icon={["fas", "circle"]} size="sm" className="text-danger mr-2" />;
        case Status.degraded:
            return <FontAwesomeIcon icon={["fas", "circle"]} size="sm" className="text-warning mr-2" />;
        case Status.maintenance:
            return <FontAwesomeIcon icon={["fas", "circle"]} size="sm" className="mr-2" />;
        case Status.online:
            return <FontAwesomeIcon icon={["fas", "circle"]} size="sm" className="text-success mr-2" />;
    }
};

const renderPlatformSubStatusIcon = (status: Status, small?: boolean) => {
    switch (status) {
        case Status.online:
            return <FontAwesomeIcon icon={["fas", "check-circle"]} className="mr-3 text-success" size={small ? "sm" : "1x"} />;
        case Status.maintenance:
        case Status.degraded:
            return <FontAwesomeIcon icon={["fas", "times-circle"]} className="mr-3 text-danger" size={small ? "sm" : "1x"} />;
        case Status.interrupted:
            return <FontAwesomeIcon icon={["fas", "exclamation-triangle"]} className="mr-3 text-warning" size={small ? "sm" : "1x"} />;
    }
};

const renderPlatformSubStatusText = (status: Status, locale: string, small?: boolean) => {
    switch (status) {
        case Status.online:
            return (
                <p className="my-0 text-success">
                    <small>{localizationApi.GetSubStatusTextOnlineLocale(locale)}</small>
                </p>
            );
        case Status.maintenance:
        case Status.degraded:
            return (
                <p className="my-0 text-danger">
                    <small>{localizationApi.GetOutageTitlebarLocale(locale)}</small>
                </p>
            );
        case Status.interrupted:
            return (
                <p className="my-0 text-warning">
                    <small>{localizationApi.GetSubStatusTextDegradedLocale(locale)}</small>
                </p>
            );
    }
};

const renderPlatformSubStatusName = (status: SubStatusKey, locale: string) => {
    switch (status) {
        case SubStatusKey.Authentication:
            return <>{localizationApi.GetAuthSubstatusLocale(locale)}</>;
        case SubStatusKey.Connectivity:
            return <>{localizationApi.GetConnectivitySubstatusLocale(locale)}</>;
        case SubStatusKey.Matchmaking:
            return <>{localizationApi.GetMatchmakingSubstatusLocale(locale)}</>;
        case SubStatusKey.Purchase:
            return <>{localizationApi.GetStoreSubstatusLocale(locale)}</>;
        case SubStatusKey.Leaderboard:
            return <>{localizationApi.GetLeaderboardSubstatusLocale(locale)}</>;
        default:
            return <></>;
    }
}

const getPlatformName = (platformType: string) => {
    switch (platformType) {
        case PlatformCode.ANDROID:
            return 'Android';
        case PlatformCode.IOS:
            return 'iOS';
        case PlatformCode.IPAD:
            return 'I-PAD';
        case PlatformCode.PC:
            return 'PC';
        case PlatformCode.PS4:
            return 'PS4';
        case PlatformCode.PS3:
            return 'PS3';
        case PlatformCode.PS5:
            return 'PS5';
        case PlatformCode.SWITCH:
            return 'SWITCH';
        case PlatformCode.XBOXONE:
            return 'XBOX ONE';
        case PlatformCode.XBOXSX:
            return 'XBOX SERIES X|S';
        case PlatformCode.XBOX360:
            return 'X360';
        case PlatformCode.LUNA:
            return 'Luna';
        case PlatformCode.NVIDIA:
            return 'GeForce Now';
        case PlatformCode.WEB:
            return 'PCWeb';
        case PlatformCode.WII:
            return 'WII';
        case PlatformCode.WIIU:
            return 'WIIU';
        case PlatformCode.OCULUS:
            return 'Oculus';
        default:
            return platformType;
    }
}

const withExceptions = (sp: string, platform: string, subStatus: number) => {
    switch (sp) {
        case "pop":
            return (sp === "pop" && platform.toLowerCase() === "switch" && subStatus === SubStatusKey.Authentication ? false : true);

        default: return true;
    }
}

const PlatformComp: React.FC<Props> = props => {
    const [hover, setHover] = useState(false);
    let data = statusPageData[props.sp_identifier];

    let platformStatus = props.platform.isMaintenance ? Status.maintenance : props.platform.status === Status[Status.interrupted] ? Status.interrupted : props.platform.status !== Status[Status.online] ? Status.degraded : Status.online;

    const platformSubStatus: Status[] = Object.keys(SubStatusKey)
        .filter(pkey => !isNaN(Number(pkey)) && data.subStatuses.find(i => i === Number(pkey)) !== undefined)
        .map(pkey =>
        (Number(pkey) === SubStatusKey.Connectivity
            ? Status[props.platform.status as keyof typeof Status]
            : platformStatus === Status.interrupted
                ? Status.interrupted
                : props.platform.impactedFeatures.some(v => v.toLowerCase() === SubStatusKey[Number(pkey)].toString().toLowerCase())
                    ? Status.degraded
                    : Status.online));

    const numberOfIssuesDetected = platformSubStatus.filter(p => p !== Status.online).length;
    const details = Object.keys(SubStatusKey)
        .filter(pkey => !isNaN(Number(pkey)) && data.subStatuses.find(i => i === Number(pkey)) !== undefined)
        .filter(pKey => withExceptions(props.sp_identifier, props.platform.platformType, Number(pKey)))
        .map(pkey => {
            let platformSubStatus = platformStatus === Status.interrupted
                ? Status.interrupted
                : props.platform.impactedFeatures.some(v => v.toLowerCase() === SubStatusKey[Number(pkey)].toString().toLowerCase())
                    ? Status.degraded
                    : Status.online;

            if (Number(pkey) === SubStatusKey.Connectivity) {
                const s: Status = Status[props.platform.status as keyof typeof Status]; // +
                const v = s === Status.degraded ? Status.interrupted : s === Status.online && props.platform.isMaintenance ? Status.maintenance : s
                platformSubStatus = v;
            }

            return (
                <li key={pkey} className={"list-group-item sub-platform d-flex flex-columns justify-content-between" + (props.platform._expanded ? "" : " hidden")}>
                    <h4 className="mb-0">
                        {renderPlatformSubStatusIcon(platformSubStatus, true)}
                        {renderPlatformSubStatusName(Number(pkey), props.locale)}
                    </h4>
                    {renderPlatformSubStatusText(platformSubStatus, props.locale)}
                </li>
            );
        });

    const platformNews =
        props.news.length === 0 && platformStatus !== Status.online ?
            (<li className="list-group-item d-flex flex-column justify-content-between news-item py-2">
                <p className="my-2">{localizationApi.GetDefaultMessageWhenIssuesLocale(props.locale)}</p>
            </li>
            ) : (
                props.news.map(newsItem => {
                    const publicationDate = new Date(newsItem.publicationDate);
                    return (
                        <li key={newsItem.newsId} className="list-group-item d-flex flex-column justify-content-between news-item py-2">
                            <p className="text-muted mb-0">
                                <small> {moment(publicationDate).format("MMM, DD")}</small>
                            </p>
                            <h4 className={"py-1 my-0"}>{newsItem.title}</h4>
                            <p className="py-2 mb-0">{newsItem.body}</p>
                        </li>
                    );
                })
            );

    return (
        <ul className="platforms list-group mb-2">
            <li className={"list-group-item d-flex flex-row justify-content-between platform list-group-item-action py-0" + (props.platform._expanded ? " expanded" : " contracted")} onClick={() => props.onToggle(props.platform)} role="button">
                <h3 className={"py-2 my-0 flex-fill text-uppercase" + (props.platform._expanded ? " expanded" : " contracted")}>
                    {!hover ? (
                        <FontAwesomeIcon
                            icon={["far", !props.platform._expanded ? "plus-square" : "minus-square"]}
                            size="sm"
                            className="text-primary mr-3"
                            onMouseOver={_ => setHover(true)}
                            onMouseOut={_ => setHover(false)}
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={["fas", !props.platform._expanded ? "plus-square" : "minus-square"]}
                            size="sm"
                            className="text-primary mr-3"
                            onMouseOver={_ => setHover(true)}
                            onMouseOut={_ => setHover(false)}
                        />
                    )}
                    {getPlatformName(props.platform.platformType)}
                </h3>
                {renderLocalizedPlatformStatus(platformStatus, numberOfIssuesDetected, props.locale)}
            </li>
            {details}
            {props.platform._expanded ? (
                <>
                    {platformNews}
                </>
            ) : null}
        </ul>
    );
};

export default PlatformComp;
